import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { useBrand } from "@/lib/brand";
import { api } from "@/lib/api";

/**
 * AskUs — the out-of-hours assistant, on the agency's own website.
 *
 * An agency gets found at the two times nobody is in: half six on a weekday,
 * when somebody has just got home and looked at a listing, and Sunday
 * afternoon. They read the page, find a contact form, and go to the next
 * agent on the portal. This asks them what they want and files it with the
 * clock running.
 *
 * NOTHING HAPPENS UNTIL SOMEBODY CLICKS. The button is drawn from a static
 * brand flag, and no conversation exists and no model is called until the
 * panel is opened. Opening it on mount would create a row and spend money
 * for every visitor who was only ever going to scroll past, which on a busy
 * site is all of them.
 *
 * AND WHEN THE ANSWER IS NO, IT IS A TELEPHONE NUMBER. Office open, switch
 * off, plan lapsed, busy day: every one of those ends with the agency's own
 * number on screen, because a visitor does not care which it was.
 */
export default function AskUs() {
  const brand = useBrand();
  // Which listing they are reading, taken off the route rather than passed
  // down through two site designs. Checked against this agency's own
  // properties on the server before it is believed, so a made-up id in the
  // address bar buys nothing.
  const { id } = useParams();
  const { pathname } = useLocation();
  const propertyId = /\/properties\/[^/]+$/.test(pathname || "") ? (id || "") : "";
  const [open, setOpen] = useState(false);
  const [convo, setConvo] = useState(null);
  const [lines, setLines] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [phone, setPhone] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const biz = brand.business || {};
  // One conversation button per page. While the agency has live chat on,
  // a person answers there (and it says when they will be back), so this
  // steps aside rather than putting two chat buttons in one corner.
  const shown = !!brand.assist && !brand.web_chat;

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: "end" });
  }, [lines, busy]);

  const begin = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/assist/start", {
        tenant_slug: brand.tenant_slug || "",
        property_id: propertyId || "",
      });
      setPhone(data.phone || biz.phone || "");
      if (!data.available) {
        setDone(true);
        setLines([{ who: "assistant", text: closedWords(data, biz) }]);
        return;
      }
      setConvo({ id: data.id, token: data.token });
      setLines([{ who: "assistant", text: data.say }]);
      // Focused after the first sentence lands, not on mount: a panel that
      // grabs the keyboard before it has said anything is a panel that
      // scrolls a phone to a text box with no context above it.
      setTimeout(() => inputRef.current?.focus(), 60);
    } catch {
      setDone(true);
      setPhone(biz.phone || "");
      setLines([{ who: "assistant", text: closedWords({}, biz) }]);
    } finally {
      setBusy(false);
    }
  }, [brand.tenant_slug, propertyId, biz]);

  const show = () => { setOpen(true); if (!lines.length) begin(); };

  const send = async (e) => {
    e?.preventDefault();
    const said = text.trim();
    if (!said || busy || done || !convo) return;
    setText("");
    setLines((l) => [...l, { who: "visitor", text: said }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assist/say", {
        id: convo.id, token: convo.token, text: said,
      });
      setLines((l) => [...l, { who: "assistant", text: data.say }]);
      if (data.phone) setPhone(data.phone);
      if (data.done) setDone(true);
    } catch {
      setLines((l) => [...l, {
        who: "assistant",
        text: `Sorry, something went wrong at our end. ${biz.phone ? `Please call ${biz.phone}.` : "Please try again shortly."}`,
      }]);
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (!shown) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={show}
        data-testid="ask-us-open"
        aria-label="Ask us a question"
        className="fixed bottom-5 right-20 z-40 h-12 px-4 rounded-full flex items-center gap-2 shadow-lg bg-primary text-primary-foreground transition-transform hover:scale-105"
      >
        <MessageSquare className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm font-semibold">Ask us</span>
      </button>
    );
  }

  return (
    <div
      data-testid="ask-us-panel"
      role="dialog"
      aria-label={`Ask ${biz.name || "us"} a question`}
      className="fixed z-50 flex flex-col shadow-2xl bg-background border border-border
                 inset-x-3 bottom-3 max-h-[80vh] rounded-lg
                 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[360px] sm:h-[520px]"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{biz.name || "Ask us"}</div>
          <div className="text-[11px] text-muted-foreground">Automated assistant</div>
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close"
                data-testid="ask-us-close"
                className="p-1 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {lines.map((l, i) => (
          <div key={i} className={l.who === "visitor" ? "flex justify-end" : "flex"}>
            <div
              data-testid={`ask-us-${l.who}`}
              className={`text-sm rounded-lg px-3 py-2 max-w-[85%] ${
                l.who === "visitor"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              }`}
            >
              {l.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex" data-testid="ask-us-thinking">
            <div className="text-sm rounded-lg px-3 py-2 bg-muted text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin inline" aria-label="Typing" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* A finished conversation ends on something to do, not on a dead
          box. The number is the agency's own. */}
      {done ? (
        <div className="px-4 py-3 border-t border-border shrink-0">
          {phone ? (
            <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="ask-us-call"
               className="block w-full text-center text-sm font-semibold rounded-sm py-2
                          bg-primary text-primary-foreground">
              Call {phone}
            </a>
          ) : (
            <p className="text-xs text-muted-foreground text-center">
              Somebody will be in touch.
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={send} className="flex gap-2 px-3 py-3 border-t border-border shrink-0">
          <label className="sr-only" htmlFor="ask-us-input">Your message</label>
          <input
            id="ask-us-input"
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            // Enter sends. Explicitly, rather than leaning on a form's
            // implicit submission: everybody presses Enter in a chat box,
            // and a box that silently swallows it looks like a box that is
            // broken. Caught here it works the same whatever wraps it.
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            maxLength={400}
            disabled={busy || !convo}
            data-testid="ask-us-input"
            placeholder="Type your question"
            className="flex-1 h-10 px-3 text-sm bg-background border border-input rounded-sm"
          />
          <button type="submit" disabled={busy || !text.trim() || !convo}
                  data-testid="ask-us-send" aria-label="Send"
                  className="w-10 h-10 rounded-sm flex items-center justify-center
                             bg-primary text-primary-foreground disabled:opacity-40">
            <Send className="w-4 h-4" />
          </button>
        </form>
      )}
    </div>
  );
}

/** What it says when it is not going to answer. Always ends in a number. */
function closedWords(data, biz) {
  const number = data.phone || biz.phone || "";
  const ring = number ? ` Please call ${number}.` : "";
  if (data.reason === "the office is open") {
    return `We're open right now, so you'll get a person rather than me.${ring}`;
  }
  if (data.reason === "busy") {
    return `Sorry, I can't take any more questions today.${ring}`;
  }
  return `Sorry, nobody is available right now.${ring || " Please try again during office hours."}`;
}
